import { useMemo } from 'react'
import { useActiveWorkspace } from '@/true-fsd/data/advertiser-account/hooks'
import { useGetContragents } from '@/shared/api'
import { WorkspaceWithLinkDescriptor, linkMethods } from '@synapex/shared2/entities'
import { ORDMarkingFormData } from '@synapex/prisma/client'
import { useMarkingPersistence } from '@/features/analytics/invite-links/ui/Marking/components/MarkingEditor/hooks'
import type { FontFamily } from '@/features/analytics/invite-links/ui/Marking/components/MarkingEditor/types'
import type { MarkDataInput } from '@/features/creative/share/marking'

const { pickLd } = linkMethods

/**
 * Тип для расширенных данных маркировки из БД
 */
type ExtendedMarkingFormData = ORDMarkingFormData & {
    mark_media_font_size?: number | null
    mark_media_alignment?: string | null
    mark_media_font_family?: string | null
    mark_media_has_background?: boolean | null
    mark_media_background_color?: string | null
    mark_media_position_x?: number | null
    mark_media_position_y?: number | null
    mark_media_canvas_width?: number | null
    mark_media_canvas_height?: number | null
    mark_media_original_width?: number | null
    mark_media_original_height?: number | null
}

/**
 * Параметры для хука useTelegramMarkingDataForShare
 */
export type UseTelegramMarkingDataForShareParams = {
    markingFormData: ORDMarkingFormData | null | undefined
    savedErid: string | null | undefined
    adOrdContragentId: number | null | undefined
    markingStatusAvailable: boolean
} & WorkspaceWithLinkDescriptor

/**
 * Хук для формирования данных маркировки для отправки креатива
 * Использует полную логику с учетом настроек редактора из БД и localStorage
 * 
 * @param params - Параметры для формирования данных маркировки
 * @returns Данные маркировки в формате MarkDataInput или undefined
 */
export const useTelegramMarkingDataForShare = ({
    markingFormData,
    savedErid,
    adOrdContragentId,
    markingStatusAvailable,
    ...wwld
}: UseTelegramMarkingDataForShareParams): MarkDataInput | undefined => {
    const { activeAwd, ordData } = useActiveWorkspace()
    
    // Для маркировки нужен рекламодатель, а не издатель
    // Используем adOrdContragentId из ordAdditionalData (рекламодатель), если он не передан явно
    const advertiserContragentId = adOrdContragentId ?? ordData.ordAdditionalData?.adOrdContragentId ?? null

    // Запрашиваем контрагентов с ролью 'advertiser' для поиска рекламодателя
    const { data: contragents } = useGetContragents({
        ...activeAwd,
        roles: ['advertiser'],
    })

    // Загружаем настройки редактора из БД и localStorage
    // Используем useMarkingPersistence для правильной загрузки данных из БД
    // link_id is 0 when optimistic link created on link creation - не делаем запрос в этом случае
    const fullWwld = useMemo(() => {
        return { ...activeAwd, ...pickLd(wwld) } as WorkspaceWithLinkDescriptor
    }, [activeAwd, wwld])

    const persistence = useMarkingPersistence(fullWwld, undefined, {
        enabled: fullWwld.link_id !== 0,
    })
    const localEditorData = useMemo(() => {
        if (!fullWwld.link_id) return null
        return persistence.loadMarkingSettings()
    }, [fullWwld.link_id, persistence.loadMarkingSettings, persistence.savedData])

    return useMemo(() => {
        // link_id is 0 when optimistic link created on link creation
        // Не выполняем логику для новых ссылок без сохраненного ID
        if (
            wwld.link_id === 0 ||
            !markingFormData ||
            !contragents ||
            !savedErid ||
            !advertiserContragentId ||
            !markingStatusAvailable
        ) {
            return undefined
        }

        // Деструктурируем базовые поля
        const { mark_media, mark_media_color, mark_text, mark_media_text } =
            markingFormData

        // Расширенные данные из БД
        const extendedData = markingFormData as ExtendedMarkingFormData

        const mark_media_font_size = extendedData.mark_media_font_size
        const mark_media_alignment = extendedData.mark_media_alignment
        const mark_media_font_family = extendedData.mark_media_font_family
        const mark_media_has_background = extendedData.mark_media_has_background
        const mark_media_background_color = extendedData.mark_media_background_color
        const mark_media_position_x = extendedData.mark_media_position_x
        const mark_media_position_y = extendedData.mark_media_position_y
        const mark_media_canvas_width = extendedData.mark_media_canvas_width
        const mark_media_canvas_height = extendedData.mark_media_canvas_height
        const mark_media_original_width = extendedData.mark_media_original_width
        const mark_media_original_height = extendedData.mark_media_original_height

        // Ищем рекламодателя по ID из ordAdditionalData
        const advertiser = contragents.find((ca) => {
            const caId = ca.ord_contragent_id
            const searchId = advertiserContragentId
            return caId === searchId || Number(caId) === Number(searchId)
        })

        if (!advertiser) {
            return undefined
        }

        const { inn, name, type } = advertiser

        if (!inn || !name) {
            return undefined
        }

        // Объединяем данные из БД и localStorage: приоритет у данных из БД
        // Если в БД нет данных, используем данные из localStorage
        const finalFontSize =
            mark_media_font_size ?? localEditorData?.fontSize
        const finalAlignment =
            mark_media_alignment ?? localEditorData?.alignment
        const finalFontFamily =
            mark_media_font_family ?? localEditorData?.fontFamily
        const finalHasBackground =
            mark_media_has_background ?? localEditorData?.hasBackground
        const finalBackgroundColor =
            mark_media_background_color ?? localEditorData?.backgroundColor
        const finalPositionX =
            mark_media_position_x ?? localEditorData?.position?.x
        const finalPositionY =
            mark_media_position_y ?? localEditorData?.position?.y

        // Собираем scale из БД или localStorage
        const finalScale =
            mark_media_canvas_width &&
            mark_media_canvas_height &&
            mark_media_original_width &&
            mark_media_original_height
                ? {
                      canvasWidth: mark_media_canvas_width,
                      canvasHeight: mark_media_canvas_height,
                      originalWidth: mark_media_original_width,
                      originalHeight: mark_media_original_height,
                  }
                : localEditorData?.scale ?? null

        return {
            erid: savedErid,
            inn,
            advertiser: name,
            type: type ?? ('physical' as const),
            mark_media,
            mark_media_color: mark_media_color ?? '#000000',
            mark_text,
            mark_media_text: mark_media_text ?? undefined,
            // Новые поля: используем данные из БД или localStorage
            mark_media_font_size:
                finalFontSize !== undefined && finalFontSize !== null
                    ? finalFontSize
                    : undefined,
            mark_media_alignment: finalAlignment
                ? (finalAlignment as 'left' | 'center' | 'right' | undefined)
                : undefined,
            mark_media_font_family: finalFontFamily
                ? (finalFontFamily as FontFamily | undefined)
                : undefined,
            mark_media_has_background:
                finalHasBackground !== undefined &&
                finalHasBackground !== null
                    ? finalHasBackground
                    : undefined,
            mark_media_background_color:
                finalBackgroundColor !== undefined &&
                finalBackgroundColor !== null
                    ? finalBackgroundColor
                    : undefined,
            mark_media_position_x:
                finalPositionX !== undefined && finalPositionX !== null
                    ? finalPositionX
                    : undefined,
            mark_media_position_y:
                finalPositionY !== undefined && finalPositionY !== null
                    ? finalPositionY
                    : undefined,
            mark_media_scale: finalScale,
        } as MarkDataInput
    }, [
        wwld.link_id,
        markingFormData,
        contragents,
        savedErid,
        advertiserContragentId,
        markingStatusAvailable,
        localEditorData,
    ])
}
